import {
  useState,
  useEffect,
  useRef,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
  FocusEvent,
} from 'react';
import { debounce } from 'lodash-es';
import { InputProps } from '../../../input/Input';

export type Props = {
  element?: React.FC<InputProps>;
  type?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  value?: string | number;
  minLength?: number;
  debounceTimeout?: number;
  forceNotifyByEnter?: boolean;
  forceNotifyOnBlur?: boolean;
  inputRef?: (el: HTMLInputElement | null) => void;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const DebounceInput: React.FC<Props> = ({
  element: Element = 'input',
  type = 'text',
  onChange,
  onKeyDown,
  onBlur,
  value: externalValue,
  minLength = 0,
  debounceTimeout = 100,
  forceNotifyByEnter = true,
  forceNotifyOnBlur = true,
  inputRef,
  ...rest
}) => {
  const [value, setValue] = useState<string | number>(externalValue ?? '');

  const isDebouncing = useRef(false);
  const debouncedFunc = useRef<((e: ChangeEvent<HTMLInputElement>) => void) | null>(null);
  const flushRef = useRef<(() => void) | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  const doNotify = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event);
    },
    [onChange]
  );

  const setupDebounce = useCallback(() => {
    if (debounceTimeout < 0) {
      debouncedFunc.current = () => null;
      return;
    }

    if (debounceTimeout === 0) {
      debouncedFunc.current = doNotify;
      return;
    }

    const debounced = debounce((event: ChangeEvent<HTMLInputElement>) => {
      isDebouncing.current = false;
      doNotify(event);
    }, debounceTimeout);

    debouncedFunc.current = (event: ChangeEvent<HTMLInputElement>) => {
      isDebouncing.current = true;
      debounced(event);
    };

    flushRef.current = () => debounced.flush();
    cancelRef.current = () => {
      isDebouncing.current = false;
      debounced.cancel();
    };
  }, [debounceTimeout, doNotify]);

  useEffect(() => {
    setupDebounce();
  }, [setupDebounce]);

  const prevExternalValue = useRef(externalValue);
  if (
    !isDebouncing.current &&
    externalValue !== undefined &&
    externalValue !== prevExternalValue.current
  ) {
    if (externalValue !== value) {
      setValue(externalValue);
    }
    prevExternalValue.current = externalValue;
  }

  useEffect(() => {
    return () => {
      flushRef.current?.();
    };
  }, []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    event.persist();
    const oldValue = value;
    const newValue = event.target.value;

    setValue(newValue);

    if (newValue.length >= minLength) {
      debouncedFunc.current?.(event);
      return;
    }

    if (String(oldValue).length > newValue.length) {
      debouncedFunc.current?.({ ...event, target: { ...event.target, value: '' } });
    }
  };

  const forceNotify = (event: ChangeEvent<HTMLInputElement>): void => {
    if (!isDebouncing.current && debounceTimeout > 0) return;
    cancelRef.current?.();

    if (String(value).length >= minLength) {
      doNotify(event);
    } else {
      doNotify({ ...event, target: { ...event.target, value: value.toString() } });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') forceNotify(event as unknown as ChangeEvent<HTMLInputElement>);

    if (onKeyDown) onKeyDown(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    forceNotify(event);

    if (onBlur) onBlur(event);
  };

  const maybeOnKeyDown = forceNotifyByEnter
    ? { onKeyDown: handleKeyDown }
    : onKeyDown
    ? { onKeyDown }
    : {};

  const maybeOnBlur = forceNotifyOnBlur ? { onBlur: handleBlur } : onBlur ? { onBlur } : {};

  const maybeRef = inputRef ? { ref: inputRef } : {};

  return (
    <Element
      {...rest}
      width="full"
      type={type}
      value={value.toString()}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange={handleChange}
      {...maybeOnKeyDown}
      {...maybeOnBlur}
      {...maybeRef}
    />
  );
};
